import {
  ARRAY_INDEX_ALL,
  formatVariableRef,
  isArrayAllIndex,
  parseVariableRef,
  variableNameKey,
} from '../../../../utils/variableScope';
import { BRACE_VAR_RE, normalizePastedVariableRef } from './pillHtml';

const namesMatch = (a, b) => variableNameKey(a) === variableNameKey(b);

/** Every variable name referenced by pills or `{{tokens}}` in a block tree. */
export const collectVariableNames = (blocks, into = new Set()) => {
  for (const block of blocks || []) {
    const html = block.content || '';
    if (html.includes('data-doc-variable') || html.includes('data-doc-math')) {
      const host = document.createElement('div');
      host.innerHTML = html;
      host.querySelectorAll('[data-doc-variable]').forEach((tag) => {
        const name = tag.getAttribute('data-var-name');
        if (name) into.add(name);
      });
      host.querySelectorAll('[data-doc-math]').forEach((tag) => {
        const expr = tag.getAttribute('data-math-expr') || '';
        BRACE_VAR_RE.lastIndex = 0;
        let match;
        while ((match = BRACE_VAR_RE.exec(expr)) !== null) {
          const { name } = normalizePastedVariableRef(match[1]);
          if (name) into.add(name);
        }
      });
    }
    // Legacy code may still hold plain {{name}} tokens (pills use data- attrs above)
    if (block.type === 'code' && html.includes('{{')) {
      BRACE_VAR_RE.lastIndex = 0;
      let match;
      while ((match = BRACE_VAR_RE.exec(html)) !== null) {
        const { name } = normalizePastedVariableRef(match[1]);
        if (name) into.add(name);
      }
    }
    if (block.children?.length) collectVariableNames(block.children, into);
  }
  return into;
};

/**
 * Rewrite `{{oldName}}` / `{{oldName[1]}}` tokens in a plain string to the
 * new name (index preserved; case-insensitive name match).
 * Returns `{ text, count }`.
 */
export const renameBraceTokens = (text, oldName, newName) => {
  const source = String(text ?? '');
  if (!source.includes('{{') || !oldName || newName == null || oldName === newName) {
    return { text: source, count: 0 };
  }
  let count = 0;
  BRACE_VAR_RE.lastIndex = 0;
  const next = source.replace(BRACE_VAR_RE, (match, rawName) => {
    const { name, index } = normalizePastedVariableRef(rawName);
    if (!name || !namesMatch(name, oldName)) return match;
    count += 1;
    return `{{${formatVariableRef(newName, index)}}}`;
  });
  return { text: next, count };
};

/**
 * Rewrite variable pills (`data-var-name`) and loose `{{tokens}}` in HTML
 * or plain content. Returns `{ content, count }`.
 */
export const renameVarsInContent = (content, oldName, newName) => {
  const source = String(content ?? '');
  if (!source || !oldName || newName == null || oldName === newName) {
    return { content: source, count: 0 };
  }

  // Plain text (typical code-block body with no tags)
  if (!source.includes('<')) {
    const { text, count } = renameBraceTokens(source, oldName, newName);
    return { content: text, count };
  }

  const host = document.createElement('div');
  host.innerHTML = source;
  let count = 0;

  host.querySelectorAll('[data-doc-variable]').forEach((tag) => {
    const name = tag.getAttribute('data-var-name') || '';
    if (!namesMatch(name, oldName)) return;
    tag.setAttribute('data-var-name', newName);
    count += 1;
    const tip = tag.getAttribute('data-var-tip');
    if (tip) {
      const { text } = renameBraceTokens(tip, oldName, newName);
      if (text !== tip) tag.setAttribute('data-var-tip', text);
    }
    const state = tag.getAttribute('data-var-state');
    const rawIndex = tag.getAttribute('data-var-index');
    const index =
      rawIndex === ARRAY_INDEX_ALL || rawIndex === '*'
        ? ARRAY_INDEX_ALL
        : rawIndex != null && rawIndex !== ''
          ? parseInt(rawIndex, 10)
          : null;
    const nextLabel = formatVariableRef(
      newName,
      isArrayAllIndex(index)
        ? ARRAY_INDEX_ALL
        : Number.isFinite(index) && index >= 1
          ? index
          : null
    );
    if (
      (state === 'unresolved' || state === 'empty') &&
      (namesMatch(tag.textContent, oldName) ||
        namesMatch(parseVariableRef(tag.textContent).name, oldName))
    ) {
      tag.textContent = nextLabel;
    }
  });

  host.querySelectorAll('[data-doc-math]').forEach((tag) => {
    const expr = tag.getAttribute('data-math-expr') || '';
    const { text, count: c } = renameBraceTokens(expr, oldName, newName);
    if (c) {
      tag.setAttribute('data-math-expr', text);
      count += c;
    }
  });

  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node = walker.nextNode();
  while (node) {
    textNodes.push(node);
    node = walker.nextNode();
  }
  textNodes.forEach((textNode) => {
    if (textNode.parentElement?.closest?.('[data-doc-variable], [data-doc-math]')) return;
    const { text, count: c } = renameBraceTokens(textNode.textContent, oldName, newName);
    if (c) {
      textNode.textContent = text;
      count += c;
    }
  });

  return { content: host.innerHTML, count };
};

const renameTableData = (tableData, oldName, newName) => {
  if (!tableData) return { tableData, count: 0 };
  let count = 0;
  let changed = false;

  const mapCell = (cell) => {
    if (Array.isArray(cell)) {
      const r = renameVarsInBlocks(cell, oldName, newName);
      if (r.count) {
        count += r.count;
        changed = true;
        return r.blocks;
      }
      return cell;
    }
    const { content, count: c } = renameVarsInContent(cell, oldName, newName);
    if (c) {
      count += c;
      changed = true;
      return content;
    }
    return cell;
  };

  const headers = (tableData.headers || []).map(mapCell);
  const rows = (tableData.rows || []).map((row) => {
    const next = (row || []).map(mapCell);
    return next;
  });

  if (!changed) return { tableData, count: 0 };
  return { tableData: { ...tableData, headers, rows }, count };
};

/**
 * Rewrite all references to `oldName` across a block tree (pills, code
 * tokens, table cells, nested children). Returns `{ blocks, count }`.
 */
export const renameVarsInBlocks = (blocks, oldName, newName) => {
  if (!oldName || newName == null || oldName === newName) {
    return { blocks: blocks || [], count: 0 };
  }
  let count = 0;

  const walk = (list) => {
    let listChanged = false;
    const nextList = (list || []).map((block) => {
      let next = block;

      if (block.content) {
        const r = renameVarsInContent(block.content, oldName, newName);
        if (r.count) {
          count += r.count;
          next = { ...next, content: r.content };
        }
      }

      if (block.props?.tableData) {
        const r = renameTableData(block.props.tableData, oldName, newName);
        if (r.count) {
          count += r.count;
          next = {
            ...next,
            props: { ...next.props, tableData: r.tableData },
          };
        }
      }

      if (
        block.type === 'linked-variable-row' &&
        namesMatch(block.props?.variableName, oldName)
      ) {
        count += 1;
        next = {
          ...next,
          props: { ...(next.props || {}), variableName: newName },
        };
      }

      if (block.children?.length) {
        const { list: kids, changed } = walk(block.children);
        if (changed) next = { ...next, children: kids };
      }

      if (next !== block) listChanged = true;
      return next;
    });
    return { list: listChanged ? nextList : list || [], changed: listChanged };
  };

  const { list } = walk(blocks);
  return { blocks: list, count };
};

/**
 * Apply a rename to variable rows: set `name` on `renamedId` (if given) and
 * rewrite `{{oldName}}` / `{{oldName[n]}}` inside value / values / description.
 * Returns `{ rows, count }` where count is nested-ref rewrites only.
 */
export const renameVarsInVariableRows = (
  rows,
  oldName,
  newName,
  { renamedId = null } = {}
) => {
  if (!oldName || newName == null || oldName === newName) {
    return { rows: rows || [], count: 0 };
  }
  let count = 0;
  const next = (rows || []).map((row) => {
    let patched = row;
    if (renamedId && row.id === renamedId && row.name !== newName) {
      patched = { ...patched, name: newName };
    }
    if (String(patched.value || '').includes('{{')) {
      const r = renameBraceTokens(patched.value, oldName, newName);
      if (r.count) {
        count += r.count;
        patched = { ...patched, value: r.text };
      }
    }
    if (Array.isArray(patched.values)) {
      let valuesChanged = false;
      const values = patched.values.map((slot) => {
        if (!String(slot || '').includes('{{')) return slot;
        const r = renameBraceTokens(slot, oldName, newName);
        if (r.count) {
          count += r.count;
          valuesChanged = true;
          return r.text;
        }
        return slot;
      });
      if (valuesChanged) patched = { ...patched, values };
    }
    if (String(patched.description || '').includes('{{')) {
      const r = renameBraceTokens(patched.description, oldName, newName);
      if (r.count) {
        count += r.count;
        patched = { ...patched, description: r.text };
      }
    }
    return patched;
  });
  return { rows: next, count };
};
